
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '@/contexts/auth/AuthContext';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface ProtectedRouteProps {
  children?: React.ReactNode;
  requiredPermissions?: string[]; // For future role-based access control
  adminOnly?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredPermissions = [],
  adminOnly = false
}) => {
  const { isAuthenticated, currentUser, loading, isAdmin } = useAuth();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);

  console.log('ProtectedRoute check:', { 
    isAuthenticated, 
    loading, 
    currentUser: !!currentUser, 
    pathname: location.pathname 
  });

  // Check for potential issues
  useEffect(() => {
    if (!loading && isAuthenticated && !currentUser) {
      setError("Authentication error: User data is missing");
    }
  }, [currentUser, isAuthenticated, loading]);

  // Show error state if there's an issue with auth state
  if (error) {
    return (
      <div className="p-4 flex flex-col items-center">
        <Alert variant="destructive" className="mb-4 max-w-md w-full">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        
        <button 
          onClick={() => window.location.reload()} 
          className="px-4 py-2 bg-primary text-primary-foreground rounded"
        >
          Reload Page
        </button>
      </div>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen flex-col">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
        <p className="text-sm text-muted-foreground">Verifying access...</p>
      </div>
    );
  }

  // If not authenticated, redirect to login page with the attempted URL in state
  if (!isAuthenticated || !currentUser) {
    console.log('Redirecting to login from:', location.pathname);
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Check for admin access if this is an admin-only route
  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" state={{ error: "Admin access required" }} replace />;
  }

  console.log('ProtectedRoute: Access granted for', location.pathname);
  return children ? <>{children}</> : <Outlet />;
};

export default ProtectedRoute;
